// src/pages/AppsPage.tsx — Phase 1 Slice B commit 6.
//
// Grouped apps grid. Three sections (Daily / For you / Manage) each render
// a 2-column grid of AppTile cards. The Manage group is filtered by perm
// (admin / news-admin / alerts-admin) and is empty for a regular volunteer.
//
// Routing:
//   - Internal routes (start with "/") → navigate(route).
//   - External routes (start with "https://") → window.open in new tab.
//
// Permission gating follows STATUS §3 (2026-05-14): me.isAdmin is the sole
// admin gate; me.isNewsAdmin / me.isAlertsAdmin remain as per-resource
// sub-flags so a volunteer granted edit access to a single resource still
// sees that one tile.
//
// Phase 9 follow-up (2026-05-21) — D2: pass me.authMethod through to
// isAppVisible so external SSO-only tiles (Reports / Events / Risks /
// Objectives) hide for PIN volunteers.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { AppTile, Section, SearchInput, TopBar } from "../components/ui";
import { FeaturedFirstAiderTile } from "../components/firstAider/FeaturedFirstAiderTile";
import {
  APPS,
  isAppVisible,
  isExternalRoute,
  type AppEntry,
  type AppGroup,
} from "../lib/apps";

const GROUPS: ReadonlyArray<{ id: AppGroup; title: string; sub: string; cols: number }> = [
  { id: "daily",     title: "Daily",     sub: "Today's essentials",       cols: 2 },
  { id: "for-you",   title: "For you",   sub: "Your stuff",               cols: 2 },
  { id: "manage",    title: "Manage",    sub: "Admin & editors",          cols: 2 },
  { id: "app-links", title: "App Links", sub: "Other NYMR apps",          cols: 4 },
];

export function AppsPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const perms = {
    isAdmin: Boolean(me?.isAdmin),
    isNewsAdmin: Boolean(me?.isNewsAdmin),
    isAlertsAdmin: Boolean(me?.isAlertsAdmin),
    apps: me?.apps,
    // Phase 9 follow-up D2 -- gate ssoOnly tiles for PIN volunteers.
    // Falls back to undefined while /api/me is still loading, which lets
    // every tile render briefly; the next render after `me` lands does the
    // hide. That's the right trade-off: better to flash a tile we'll hide
    // than to hide a tile we shouldn't.
    authMethod: me?.authMethod,
    // Sign-In System (Build 1) -- SSO-admin gate (System Preferences) + feature
    // kill-switches + the Sign In Timesheet audience (admin / HR / sign-in-enabled).
    isSsoAdmin: Boolean(me?.isSsoAdmin),
    issuesManager: Boolean(me?.issues?.manager),
    isHrAdmin: Boolean(me?.isHrAdmin),
    signInEnabled: Boolean(me?.signIn?.enabled),
    featureFlags: me?.features,
    isDvCheckAdmin: Boolean(me?.isDvCheckAdmin),
    isLongServiceAdmin: Boolean(me?.isLongServiceAdmin),
    isMoorslineEditor: Boolean(me?.isMoorslineEditor),
    isNewsletterPublisher: Boolean(me?.isNewsletterPublisher),
    isTrainingAdmin: Boolean(me?.isTrainingAdmin),
    isIncidentAdmin: Boolean(me?.isIncidentAdmin),
  };

  const visible = APPS.filter((a) => isAppVisible(a, perms));

  // Live app search — instantly filters the visible tiles by name/description.
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const filtered = searching
    ? visible.filter(
        (a) => a.name.toLowerCase().includes(q) || (a.desc ?? "").toLowerCase().includes(q),
      )
    : visible;

  function openApp(app: AppEntry) {
    if (isExternalRoute(app.route)) {
      // Slice G — every external tile opens in a new tab with rel-noopener
      // so the destination can't grab the opener window. Previously Live
      // Trains used window.location.href which navigated away from the hub.
      window.open(app.route, "_blank", "noopener,noreferrer");
    } else {
      navigate(app.route);
    }
  }

  return (
    <div>
      <TopBar title="Apps" sub="Everything available to you" />
      <div style={{ padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
        {/* Live app search — type to instantly filter the tiles below. */}
        <SearchInput value={query} onChange={setQuery} placeholder="Search apps…" />
        {/* Phase 8 — featured red "Call to First Aiders" tile. Now a User Access
            grant (apps.firstAiderCallout, OFF by default): shown only to admins
            or users who hold the grant, AND only while the system feature flag
            is on. `features.firstAiderCallout !== false` keeps the system gate
            lenient across a stale /api/me; the per-user grant must be explicit.
            Hidden while searching so the results read as a clean app list. */}
        {!searching
          && me?.features?.firstAiderCallout !== false
          && (me?.role === "admin" || me?.apps?.firstAiderCallout === true)
          && <FeaturedFirstAiderTile />}
        {GROUPS.map((group) => {
          const groupApps = filtered.filter((a) => a.group === group.id);
          if (groupApps.length === 0) return null;
          return (
            <div key={group.id}>
              <Section title={group.title} sub={group.sub} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${group.cols}, 1fr)`,
                  gap: group.cols === 4 ? 10 : 12,
                }}
              >
                {groupApps.map((a) => (
                  <AppTile
                    key={a.id}
                    name={a.name}
                    desc={a.desc}
                    iconKey={a.iconKey}
                    tone={a.tone}
                    size={a.size}
                    onTap={() => openApp(a)}
                  />
                ))}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p style={{ color: C.inkSoft, fontFamily: F.body, fontWeight: 600 }}>
            {searching
              ? `No apps match “${query.trim()}”.`
              : "No apps available for your role yet."}
          </p>
        )}
      </div>
    </div>
  );
}
